
import * as d3 from "../lib/d3.js";
import {clamp, distance} from "../util/math.js";
import {$} from "../util/seq.js";
import {isUnfriendlyIFrameContext} from "../util/context.js";
import {deconstruct} from "../projection/projections.js";
import {coalesceNum} from "../util/values.js";

const MIN_MOVE = 6;         // slack before a drag operation beings (pixels)
const MOVE_END_WAIT = 750;  // time to wait for a move operation to be considered done (millis)

/**
 * The input controller is an object that translates move operations (drag and/or zoom) into mutations of the
 * current globe's projection, and emits events so other page components can react to these move operations.
 *
 * D3's built-in Zoom behavior is used to bind to the document's drag/zoom events, and the input controller
 * interprets D3's events as move operations on the globe. This method is complicated due to the complex
 * event behavior that occurs during drag and zoom.
 *
 * D3 move operations usually occur as "start" -> ("zoom")* -> "end" event chain. During "zoom" events
 * the scale and mouse may change, implying a zoom or drag operation accordingly. These operations are quite
 * noisy. What should otherwise be one smooth continuous zoom is usually comprised of several "start" ->
 * "zoom" -> "end" event chains. A debouncer is used to eliminate the noise by waiting a short period of
 * time to ensure the user has finished the move operation.
 *
 * The "zoom" events may not occur; a simple click operation occurs as either "start" -> "end" or "start" ->
 * "zoom" -> "end". There is additional logic for other corner cases, such as spurious drags which move the globe
 * just a few pixels (most likely unintentional), and the tendency for some touch devices to issue events out of
 * order: "zoom" -> "start" -> "end" (though this may no longer occur with D3 v4).
 *
 * This object emits clean "moveStart" -> ("move")* -> "moveEnd" events for move/zoom operations, and "click"
 * events for everything else.
 *
 * @param {Handle} orientation
 * @param viewboxAgent
 * @param {EarthClient?} client
 * @returns {Object}
 */
export function buildInputController(orientation, viewboxAgent, client) {

    const dispatch = d3.dispatch("moveStart", "move", "moveEnd", "click");
    let globe = undefined;
    let op = null
    let prevClick = {time: 0, mouse: [0, 0]};
    let reorientInProgress = false;

    /**
     * @returns {Object} an object to represent the state for one move operation.
     */
    function newOp(startMouse, startScale) {
        return {
            type: "click",  // initially assumed to be a click operation
            startMouse,
            startScale,
            manipulator: globe.manipulator(startMouse, startScale)
        };
    }

    function start(event) {
        op = op ?? newOp(d3.pointers(event)[0] ?? [NaN, NaN], globe.projection.scale());  // a new operation begins
    }

    function step(event) {
        const currentMouse = d3.pointers(event)[0] ?? [NaN, NaN];
        const currentScale = event.transform?.k ?? globe.projection.scale();
        op = op ?? newOp(currentMouse, 1);  // Fix bug on some browsers where zoomstart fires out of order.
        if (op.type === "click") {
            const distanceMoved = distance(currentMouse, op.startMouse);
            if (currentScale === op.startScale && (distanceMoved < MIN_MOVE || isNaN(distanceMoved))) {
                // to reduce annoyance, ignore op if mouse has barely moved and no zoom is occurring
                return;
            }
            emitMoveStart();
            op.type = "drag";
        }
        if (currentScale !== op.startScale || isNaN(currentMouse[0])) {
            // Whenever a scale change is detected, or mouse is undefined (such as during double-click),
            // stickily switch to a zoom operation.
            op.type = "zoom";
        }

        // when zooming, ignore whatever the mouse is doing--really cleans up behavior on touch devices
        op.manipulator.move(op.type === "zoom" ? null : currentMouse, currentScale);
        emitMove();
    }

    function shouldSuppressClickEvents() {
        // Ignore clicks that occur soon after the previous click and in the same location. Reduces noise
        // on touch devices where taps trigger both touchstart and mousedown events (i.e., two clicks).
        // CONSIDER: is this still necessary after d3 v4 upgrade?
        // Also, reorientation can emit zoom start + end. Make sure not to interpret that as a click.
        return (
            (Date.now() - prevClick.time < 500) && distance(prevClick.mouse, op.startMouse) < MIN_MOVE ||
            reorientInProgress
        );
    }

    function end() {
        if (op === null) return;
        op.manipulator.end();
        if (op.type === "click") {
            if (!shouldSuppressClickEvents()) {
                emitClick();
                prevClick = {time: Date.now(), mouse: op.startMouse};
            }
        } else {
            scheduleMoveEnd();
        }
        op = null;  // the drag/zoom/click operation is over
    }

    let moveEnding = null;
    function scheduleMoveEnd() {
        if (moveEnding) {
            clearTimeout(moveEnding);
        }
        moveEnding = setTimeout(() => {
            moveEnding = null;
            if (!op || op.type !== "drag" && op.type !== "zoom") {
                orientation.set(globe.getOrientation(), {source: "moveEnd"});
                emitMoveEnd();
            }
        }, MOVE_END_WAIT);  // wait for a bit to decide if user has stopped moving the globe
    }

    function emitClick() {
        const point = op.startMouse;
        const coords = globe.projection.invert(point) ?? [NaN, NaN];
        dispatch.call("click", null, point, coords);
    }

    function emitMoveStart() {
        client?.reorientStart(deconstruct(globe.projection));
        dispatch.call("moveStart");
    }

    function emitMove() {
        client?.reorientStep(deconstruct(globe.projection));
        dispatch.call("move");
    }

    function emitMoveEnd() {
        client?.reorientEnd(deconstruct(globe.projection));
        dispatch.call("moveEnd");
    }

    const zoom = d3.zoom()
        .on("start", start)
        .on("zoom", step)
        .on("end", end);

    const drag = d3.drag()
        .on("start", start)
        .on("drag", step)
        .on("end", end);

    // desktop: scroll -> zoom,   drag -> drag
    // mobile:   pinch -> zoom, scroll -> drag
    const display = d3.select("#display");
    if (isUnfriendlyIFrameContext()) {
        display.call(drag);
    } else {
        display.call(zoom)
            .on($`wheel`, event => event.preventDefault());  // prevent scrolling even when at scale extent
    }

    // CONSIDER: combine with reorient, the only real different being the support for "default" scale and
    //           rotation (which the reorient function could be modified to support).
    function internalReorient(meta) {
        if (!globe || meta?.source === "moveEnd") {
            // reorientation occurred because the user just finished a move operation, so globe is already
            // oriented correctly.
            return;
        }
        const originalValue = reorientInProgress;
        reorientInProgress = true;
        try {
            emitMoveStart();
            globe.setOrientation(orientation.get(), viewboxAgent.value());
            zoom.transform(display, d3.zoomIdentity.scale(globe.projection.scale()));
            emitMoveEnd();
        } finally {
            reorientInProgress = originalValue;
        }
    }

    // CONSIDER: the globe object (or something like it) should just be a wrapper for the D3 projection, and this
    //           method is just a fancier way of manipulating the projection compared to D3's standard methods. The
    //           inputController supports many kinds of gestures, not all of which manipulate the projection.
    function reorient(op = {}) {
        if (!globe) {
            return;
        }

        const proj = globe.projection;
        const useDefaultScale = op.scale === "default";
        const useDefaultRotate = op.rotate === "default";
        const useDefaultTranslate = op.translate === "default";
        const defaultProj = (useDefaultScale || useDefaultRotate || useDefaultTranslate) ?
            globe.newProjection(viewboxAgent.value()) :
            undefined;

        const oldScale = proj.scale();
        const baseScale = useDefaultScale ? defaultProj.scale() : coalesceNum(op.scale, oldScale);
        const newScale = clamp(baseScale * coalesceNum(op.scaleBy, 1), ...globe.scaleExtent());

        const rotate = useDefaultRotate ? defaultProj.rotate() : proj.rotate();
        if (Array.isArray(op.rotate)) {
            rotate[0] = coalesceNum(op.rotate[0], rotate[0]);
            rotate[1] = coalesceNum(op.rotate[1], rotate[1]);
            rotate[2] = coalesceNum(op.rotate[2], rotate[2]);
        }
        if (Array.isArray(op.rotateBy)) {
            rotate[0] += coalesceNum(op.rotateBy[0], 0);
            rotate[1] += coalesceNum(op.rotateBy[1], 0);
            rotate[2] += coalesceNum(op.rotateBy[2], 0);
        }

        const translate = useDefaultTranslate ? defaultProj.translate() : proj.translate();
        if (Array.isArray(op.translate)) {
            translate[0] = coalesceNum(op.translate[0], translate[0]);
            translate[1] = coalesceNum(op.translate[1], translate[1]);
        }
        if (Array.isArray(op.translateBy)) {
            translate[0] += coalesceNum(op.translateBy[0], 0);
            translate[1] += coalesceNum(op.translateBy[1], 0);
        }

        const originalValue = reorientInProgress;
        reorientInProgress = true;
        try {
            if (!moveEnding) {
                emitMoveStart();
            }

            proj.rotate(rotate).translate(translate);

            if (newScale !== oldScale) {
                proj.scale(newScale);
                zoom.transform(display, d3.zoomIdentity.scale(newScale));
            }

            emitMove();
            scheduleMoveEnd();
        } finally {
            reorientInProgress = originalValue;
        }
    }

    orientation.on($`change`, (delta, old, meta) => internalReorient(meta));

    viewboxAgent.on($`update`, (viewbox, _, previousViewbox) => {
        if (!globe) {
            return;
        }
        // Changing the viewbox is a type of reorientation which adjusts the scale and translation of the projection.
        // Reinterpret the translation as an offset from the center. Because the center has moved, the translation
        // must change as well.
        const {scale = "default", translate = []} = orientation.get() ?? {};
        const [cx, cy] = globe.center(previousViewbox);
        const x = translate[0] ?? cx;
        const y = translate[1] ?? cy;
        const Δx = x - cx;
        const Δy = y - cy;
        const [nx, ny] = globe.center(viewbox);
        reorient({scale, translate: [nx + Δx, ny + Δy]});
    });

    return Object.assign(dispatch, {
        assignGlobe(x) {
            globe = x;
            zoom.scaleExtent(globe.scaleExtent());
            internalReorient();
        },
        reorient,
        cancelMove() {
            // Forcefully end the current move operation, if any.
            end();
        },
    });
}
